import React from 'react';
import { Dispatch } from 'redux';
import { IAutomationLogsList } from '../interface/automationLogsList';
import timezone from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import SVGWrapper from '../../../../../../../../../common/icons/svg/svg-wrapper';
import ConfigService from '../../../../../../../../../common/config';
import Axios from 'axios';

interface IAutomationsLogsProps {
  list: IAutomationLogsList[];
  tz: string;
  company: string;
  formId: string;
  dispatch: Dispatch;
}

const AutomationLogs: React.FC<IAutomationsLogsProps> = (props) => {

  const renderStatus = (status: string): string => {
    let _status = status.toLocaleUpperCase();
    switch (_status) {
      case 'NO_ACTION':
        return 'NO ACTION PERFORMED';
      default:
        return _status;
    }
  }

  const renderForms = (details: any, name: string, returnObj: boolean = false) => {
    if (!details || (details && !details.hasOwnProperty('urn')
      && !details.hasOwnProperty('id') && !details.hasOwnProperty('formId'))) {
      return null;
    }
    if (name.includes('Doc')) {
      name = 'Affected Form'
    }
    if (returnObj) {
      return `${name} : ${details.urn}`;
    }
    return <span key={uuid()}>
      <span>{name} : </span>
      <span className={`document-urn-link`}>
        <a target='_blank' rel="noopener noreferrer" href={`https://cloud.mobbizapps.com/#/docs/${details.urn.replace(/#/g, '')}`}>
          {details.urn}
        </a>
      </span>
    </span>
  }

  const renderAffectedForms = (affectedForms: any, name: string, returnObj: boolean = false) => {
    if (typeof affectedForms === 'object') {
      if (Array.isArray(affectedForms)) {
        return affectedForms.map(forms => {
          return renderForms(forms, name, returnObj);
        });
      }
      return renderForms(affectedForms, name, returnObj);
    }
    return affectedForms;
  }

  const renderDate = (date: any): string => {
    return timezone(date).tz(props.tz).format('lll');
  }

  const escapeCSVValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const convertToCSV = (arr: any) => {
    const headers = Object.keys(arr[0]).map(escapeCSVValue).join(',') + '\n';
    console.log(arr);
    const rows = arr.map((obj: any) => Object.values(obj).map(escapeCSVValue).join(',') + '\n');
    return headers + rows.join('');
  };

  const exportFile = (data: any) => {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `automation-logs.csv`);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchLogs = async (): Promise<IAutomationLogsList[]> => {
    const axiosURL = ConfigService.loadConfig().general.apiBaseUrl + `/${props.company}/forms/${props.formId}/automationLogs?offset=0&limit=0`;
    try {
      const response = await Axios.get(axiosURL);
      const data = response.data.data as IAutomationLogsList[];
      return data;
    } catch (error) {
      return [];
    }
  }

  const exportLogs = async () => {
    const data = await fetchLogs();
    const json = data.map((item) => {
      const affectedFormKeys = ['triggerDoc', 'errorName', 'message'];
      return {
        'Date': renderDate(item.createdAt),
        'Rule': item.name,
        'Status': renderStatus(item.status),
        'Duration': `${item.duration.toFixed(2)}s`,
        'AffectedForms': affectedFormKeys.map((name) => {
          if (!item.context[name]) return null;
          return renderAffectedForms(item.context[name], name, true);
        }).filter((e) => e !== null).join(', ')
      }
    });
    exportFile(json);
  }

  return (
    <div className='automation-logs-container'>
      <span className={`btn`} title='Export Automation Logs' onClick={exportLogs}>
        <SVGWrapper iconName='exportToFile' width='25' color='#3a85a8' />
        <span className='title'>Export Logs</span>
      </span>
      <table className='table-container automation-logs-table'>
        <thead>
          <tr>
            <th className='th-group'>Date</th>
            <th className='th-group'>Rule</th>
            <th className='th-group'>Status</th>
            <th className='th-group'>Duration</th>
            <th className='th-group'>Affected forms</th>
          </tr>
        </thead>
        <tbody>
          {props.list && props.list.map(({ id, createdAt, name, status, context, duration }: IAutomationLogsList, idx: number) => {
            const affectedFormKeys = ['triggerDoc', 'errorName', 'message'];
            return (
              <tr key={`${id}-${idx}`}>
                <td>{renderDate(createdAt)}</td>
                <td>{name}</td>
                <td><span className={`automation-logs-status-container automation-status-${status.replace('_', '-')}`}>{renderStatus(status)}</span></td>
                <td>{`${duration.toFixed(2)}s`}</td>
                <td>
                  {affectedFormKeys.map((name) => {
                    if (!context[name]) return null;

                    return <p className={`context ${name.includes('error') ? 'error' : ''}`} key={name}>
                      {renderAffectedForms(context[name], name)}
                    </p>
                  })}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  );
}

export default React.memo(AutomationLogs);
